import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  ElementItemDto,
  VehicleInfoItemDto,
  WarrantyItemDto,
} from '@/features/delivery-proof/types/delivery-proof.types';

export interface DeliveryProofFormData {
  contractNum: string;
  /** UUID du brouillon de contrat source (pas le draft d'attestation) */
  draftUuid?: string;
  dpNumber?: string;
  societyId?: string;
  userId?: string;
  userName?: string;
  warranties: WarrantyItemDto[];
  hidePrices: boolean;
  elements: ElementItemDto[];
  vehicleInfo: VehicleInfoItemDto[];
}

interface DeliveryProofStore {
  // État
  currentStep: number;
  deliveryProofId: string | null;
  formData: DeliveryProofFormData;
  isDirty: boolean;

  // Actions
  setStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  setDeliveryProofId: (id: string) => void;
  updateFormData: (data: Partial<DeliveryProofFormData>) => void;
  setFormData: (data: DeliveryProofFormData) => void;
  markClean: () => void;
  markDirty: () => void;
  reset: () => void;
}

const initialFormData: DeliveryProofFormData = {
  contractNum: '',
  dpNumber: undefined,
  societyId: undefined,
  userId: undefined,
  userName: undefined,
  warranties: [],
  hidePrices: false,
  elements: [],
  vehicleInfo: [],
};

export const useDeliveryProofStore = create<DeliveryProofStore>()(
  devtools(
    (set) => ({
      currentStep: 1,
      deliveryProofId: null,
      formData: initialFormData,
      isDirty: false,

      setStep: (step) =>
        set({ currentStep: step }, false, 'deliveryProof/setStep'),

      nextStep: () =>
        set(
          (state) => ({ currentStep: Math.min(state.currentStep + 1, 4) }),
          false,
          'deliveryProof/nextStep'
        ),

      previousStep: () =>
        set(
          (state) => ({ currentStep: Math.max(state.currentStep - 1, 1) }),
          false,
          'deliveryProof/previousStep'
        ),

      setDeliveryProofId: (id) =>
        set({ deliveryProofId: id }, false, 'deliveryProof/setId'),

      updateFormData: (data) =>
        set(
          (state) => ({
            formData: { ...state.formData, ...data },
            isDirty: true,
          }),
          false,
          'deliveryProof/updateFormData'
        ),

      setFormData: (data) =>
        set(
          { formData: data, isDirty: false },
          false,
          'deliveryProof/setFormData'
        ),

      markClean: () =>
        set({ isDirty: false }, false, 'deliveryProof/markClean'),

      markDirty: () =>
        set({ isDirty: true }, false, 'deliveryProof/markDirty'),

      reset: () =>
        set(
          {
            currentStep: 1,
            deliveryProofId: null,
            formData: initialFormData,
            isDirty: false,
          },
          false,
          'deliveryProof/reset'
        ),
    }),
    { name: 'DeliveryProofStore' }
  )
);
